/**
 * SimilarTransactionsModal Component
 * Modal for applying a category to similar uncategorized transactions
 * with optional rule creation for future transactions
 * Based on WP-CAT-2: Apply to Similar Transactions Feature
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Spinner, Alert } from '@/components/ui';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import { useSimilarTransactions, useBulkUpdateWithRule } from '@/hooks/useSimilarTransactions';
import { ApiClientError } from '@/services/api';
import type { SimilarTransactionItem } from '@/services/similarTransactions';

// ============================================================================
// Types
// ============================================================================

export interface SimilarTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalTransaction: {
    id: string;
    name: string;
    amount: number;
  };
  newCategoryId: string;
  newCategoryName: string;
  onApply: (result: ApplyResult) => void;
}

export interface ApplyResult {
  appliedCount: number;
  ruleCreated: boolean;
  ruleId?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function SimilarTransactionsModal({
  isOpen,
  onClose,
  originalTransaction,
  newCategoryId,
  newCategoryName,
  onApply,
}: SimilarTransactionsModalProps) {
  // Fetch similar transactions
  const {
    similarTransactions,
    suggestedPattern,
    isLoading,
    isError,
    error,
  } = useSimilarTransactions({
    transactionId: originalTransaction.id,
    enabled: isOpen,
  });

  // State for selection and rule creation
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createRule, setCreateRule] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Mutation for bulk update
  const bulkUpdateMutation = useBulkUpdateWithRule();

  // A rule is offered only for a suggestion. None for a person-to-person payment
  // (WP-SEC-20): its "merchant" is a person's name. The server also refuses to
  // generate one, so a blank rulePattern can never create it either.
  const canCreateRule = Boolean(suggestedPattern);
  const willCreateRule = canCreateRule && createRule;

  // Determine if original transaction is a debit (expense) or credit (income)
  const originalIsDebit = originalTransaction.amount < 0;

  // Smart defaults: pre-select transactions with same amount sign
  useEffect(() => {
    if (similarTransactions.length > 0) {
      const preselected = new Set<string>();
      similarTransactions.forEach((txn) => {
        const txnIsDebit = txn.amount < 0;
        // Pre-select if same sign (both debits or both credits)
        if (txnIsDebit === originalIsDebit) {
          preselected.add(txn.id);
        }
      });
      setSelectedIds(preselected);
    }
  }, [similarTransactions, originalIsDebit]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setCreateRule(false);
      setApiError(null);
    }
  }, [isOpen]);

  // Calculate total amount of selected transactions
  const selectedTotal = useMemo(() => {
    let total = 0;
    similarTransactions.forEach((txn) => {
      if (selectedIds.has(txn.id)) {
        total += Math.abs(txn.amount);
      }
    });
    return total;
  }, [similarTransactions, selectedIds]);

  // Handlers
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === similarTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(similarTransactions.map((t) => t.id)));
    }
  }, [selectedIds.size, similarTransactions]);

  const handleToggleTransaction = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (selectedIds.size === 0) {
      onClose();
      return;
    }

    setApiError(null);

    try {
      const result = await bulkUpdateMutation.mutateAsync({
        transactionIds: Array.from(selectedIds),
        categoryId: newCategoryId,
        createRule: willCreateRule,
        rulePattern: willCreateRule && suggestedPattern ? suggestedPattern : undefined,
        // No ruleName: the server generates "Auto: <pattern>" and cuts it to the
        // column length; a client-built name could exceed ruleName's 100-character
        // limit and fail the whole request with 422 (WP-BUG-65 F6).
      });

      onApply({
        appliedCount: result.updatedCount,
        ruleCreated: result.ruleCreated,
        ruleId: result.ruleId,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.detail);
      } else if (err instanceof Error) {
        setApiError(err.message);
      } else {
        setApiError('An unexpected error occurred. Please try again.');
      }
    }
  }, [selectedIds, newCategoryId, willCreateRule, suggestedPattern, bulkUpdateMutation, onApply, onClose]);

  const handleSkip = useCallback(() => {
    onClose();
  }, [onClose]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <ModalWrapper onClose={onClose}>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Finding similar transactions...</p>
        </div>
      </ModalWrapper>
    );
  }

  // Error state
  if (isError) {
    return (
      <ModalWrapper onClose={onClose}>
        <Alert variant="error" className="mb-4">
          {error?.message ?? 'Failed to find similar transactions'}
        </Alert>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalWrapper>
    );
  }

  // No similar transactions found - close immediately
  if (similarTransactions.length === 0) {
    // Call onClose via useEffect to avoid setState during render
    return null;
  }

  return (
    <ModalWrapper onClose={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Apply Category to Similar Transactions
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          aria-label="Close"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Summary */}
      <p className="text-sm text-gray-600 mb-4">
        You categorized <span className="font-medium text-gray-900">"{originalTransaction.name}"</span>{' '}
        as <span className="font-medium text-primary-600">{newCategoryName}</span>
      </p>

      {/* Count */}
      <p className="text-sm text-gray-700 mb-3">
        Found <span className="font-semibold">{similarTransactions.length}</span> similar uncategorized
        transaction{similarTransactions.length !== 1 ? 's' : ''}:
      </p>

      {/* API Error */}
      {apiError && (
        <Alert variant="error" className="mb-4" onDismiss={() => setApiError(null)}>
          {apiError}
        </Alert>
      )}

      {/* Transaction list */}
      <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
        {/* Select all header */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedIds.size === similarTransactions.length}
              onChange={handleSelectAll}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {selectedIds.size === similarTransactions.length ? 'Deselect All' : 'Select All'}
            </span>
          </label>
        </div>

        {/* Scrollable transaction list */}
        <div className="max-h-64 overflow-y-auto">
          {similarTransactions.map((txn) => (
            <TransactionItem
              key={txn.id}
              transaction={txn}
              isSelected={selectedIds.has(txn.id)}
              onToggle={() => handleToggleTransaction(txn.id)}
              originalIsDebit={originalIsDebit}
            />
          ))}
        </div>
      </div>

      {/* Selected total */}
      {selectedIds.size > 0 && (
        <p className="text-sm text-gray-500 mb-4">
          Total amount: <span className="font-medium">{formatCurrency(selectedTotal)}</span>
        </p>
      )}

      {/* Rule creation checkbox: only when there is a suggestion (WP-SEC-20) */}
      {canCreateRule && (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={createRule}
            onChange={(e) => setCreateRule(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div className="text-sm">
            <p className="font-medium text-gray-700">
              Create rule: Transactions containing "{suggestedPattern}"
            </p>
            <p className="text-gray-500 mt-0.5">
              will be automatically categorized as{' '}
              <span className="font-medium text-primary-600">{newCategoryName}</span>
            </p>
          </div>
        </label>
      </div>
      )}

      {/* Footer buttons */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={handleSkip}
          disabled={bulkUpdateMutation.isPending}
        >
          Skip
        </Button>
        <Button
          onClick={handleApply}
          disabled={selectedIds.size === 0 || bulkUpdateMutation.isPending}
          isLoading={bulkUpdateMutation.isPending}
        >
          Apply to {selectedIds.size} Selected
        </Button>
      </div>
    </ModalWrapper>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Modal wrapper with backdrop
 */
interface ModalWrapperProps {
  children: React.ReactNode;
  onClose: () => void;
}

function ModalWrapper({ children, onClose }: ModalWrapperProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="similar-transactions-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

/**
 * Single transaction item in the list
 */
interface TransactionItemProps {
  transaction: SimilarTransactionItem;
  isSelected: boolean;
  onToggle: () => void;
  originalIsDebit: boolean;
}

function TransactionItem({
  transaction,
  isSelected,
  onToggle,
  originalIsDebit,
}: TransactionItemProps) {
  const txnIsDebit = transaction.amount < 0;
  const isOppositeSign = txnIsDebit !== originalIsDebit;
  const displayName = transaction.merchantName || transaction.name || 'Unknown';

  // Format date safely
  let formattedDate = 'Unknown date';
  try {
    if (transaction.date) {
      formattedDate = formatDate(transaction.date, { month: 'short', day: 'numeric', year: 'numeric' });
    }
  } catch {
    // Keep default
  }

  return (
    <div
      className={cn(
        'flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0',
        isSelected && 'bg-primary-50'
      )}
    >
      <label className="flex items-center w-full gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {displayName}
          </p>
          <p className="text-xs text-gray-500">
            {formattedDate}
            {transaction.accountName && (
              <>
                <span className="mx-1">-</span>
                {transaction.accountName}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm font-medium whitespace-nowrap',
              txnIsDebit ? 'text-gray-900' : 'text-green-600'
            )}
          >
            {txnIsDebit ? '-' : '+'}
            {formatCurrency(Math.abs(transaction.amount))}
          </span>
          {isOppositeSign && (
            <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded">
              {txnIsDebit ? 'debit' : 'credit'}
            </span>
          )}
        </div>
      </label>
    </div>
  );
}
